import { useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useBalanceEditor, STAT_COLUMNS } from "@/lib/use-balance-editor";
import { LEVEL_FIELDS } from "@/lib/balance-schema";

const LEVEL_LABELS = ["Hero EXP", "Enemy EXP", "Enemy HP", "Enemy damage", "Enemy gold", "Enemy armor"];

export function BalanceRoute() {
  const editor = useBalanceEditor();
  const [tab, setTab] = useState<"levels" | "stats">("levels");
  const [filter, setFilter] = useState("");
  const data = editor.base?.data;
  const cell = (field: string, index: number, value: number, label: string) => {
    const id = `${field}:${index}`;
    const text = editor.cellValue(id, value);
    const invalid = text.trim() === "" || !Number.isFinite(Number(text));
    return <Input aria-label={label} aria-invalid={invalid} inputMode="decimal" value={text}
      disabled={editor.save.isPending} onChange={event => editor.edit(id, event.target.value)}
      className={`h-8 min-w-[105px] text-right tabular-nums ${invalid ? "border-destructive" : ""}`} />;
  };
  return <AppShell title="Game Balance" subtitle="Level scaling and status caps. Changes reach players on their next game startup.">
    <div className="space-y-4 max-w-7xl">
      {editor.query.isLoading && <Skeleton className="h-64 w-full" />}
      {editor.query.isError && <Alert variant="destructive"><AlertDescription>
        Could not load balance data: {String(editor.query.error)}
        <Button variant="ghost" size="sm" onClick={() => editor.query.refetch()}>Retry</Button>
      </AlertDescription></Alert>}
      {data && <>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4">
          <div className="space-y-1">
            <p className="text-sm font-medium">{data.expTable.length} level rows · {data.statData.length} stats</p>
            <p className="text-xs text-muted-foreground">Last saved {new Date(editor.base!.updatedAt).toLocaleString()}</p>
          </div>
          <div className="flex items-center gap-2">
            {editor.dirty && <Badge variant="outline">Unsaved changes</Badge>}
            {editor.save.isSuccess && !editor.dirty && <span role="status" className="text-sm text-green-500">Saved to backend</span>}
            <Button variant="ghost" disabled={!editor.dirty || editor.save.isPending} onClick={editor.reset}>Reset</Button>
            <Button disabled={!editor.dirty || editor.issues.length > 0 || editor.save.isPending} onClick={() => editor.save.mutate()}>
              {editor.save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
        {editor.save.error && <Alert variant="destructive"><AlertDescription>Could not save: {String(editor.save.error)}</AlertDescription></Alert>}
        {editor.issues.length > 0 && <Alert variant="destructive"><AlertDescription>
          <p className="font-medium">Fix these values before saving:</p>
          <ul className="list-disc pl-5">{editor.issues.map(issue => <li key={issue}>{issue}</li>)}</ul>
        </AlertDescription></Alert>}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2" role="tablist" aria-label="Balance tables">
            <Button role="tab" aria-selected={tab === "levels"} variant={tab === "levels" ? "secondary" : "ghost"} onClick={() => {setTab("levels");setFilter("");}}>Level Table</Button>
            <Button role="tab" aria-selected={tab === "stats"} variant={tab === "stats" ? "secondary" : "ghost"} onClick={() => {setTab("stats");setFilter("");}}>Status Caps</Button>
          </div>
          <Input aria-label={tab === "levels" ? "Find level" : "Find stat"} placeholder={tab === "levels" ? "Find level…" : "Find stat…"} value={filter} onChange={event => setFilter(event.target.value)} className="w-48" />
        </div>
        <p className="text-xs text-muted-foreground">{tab === "levels"
          ? `Maximum playable level: ${data.expTable.length - 1}. The final row is retained for the game's EXP boundary.`
          : "−1 keeps the existing unset / unlimited behavior. Stat order is fixed to match the game."}</p>
        <div role="tabpanel" aria-label={tab === "levels" ? "Level Table" : "Status Caps"} className="max-h-[65vh] overflow-auto rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-card text-xs text-muted-foreground"><tr>
              <th scope="col" className="p-3 text-left">{tab === "levels" ? "Level" : "Stat"}</th>
              {(tab === "levels" ? LEVEL_LABELS : STAT_COLUMNS.map(([, label]) => label)).map(label => <th scope="col" key={label} className="p-3 text-right font-medium">{label}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-border/50">
              {tab === "levels" ? data.expTable.map((_, index) => !String(index + 1).includes(filter.trim()) ? null : <tr key={index}>
                <th scope="row" className="px-3 py-2 text-left font-medium whitespace-nowrap">{index + 1}{index === data.expTable.length - 1 && <span className="block text-[10px] text-muted-foreground">Terminal row</span>}</th>
                {LEVEL_FIELDS.map((field, column) => <td key={field} className="p-2">{cell(field, index, data[field][index], `Level ${index + 1} ${LEVEL_LABELS[column]}`)}</td>)}
              </tr>) : data.statData.map((row, index) => !row.statName.toLowerCase().includes(filter.trim().toLowerCase()) ? null : <tr key={index}>
                <th scope="row" className="px-3 py-2 text-left font-medium whitespace-nowrap">{row.statName}</th>
                {STAT_COLUMNS.map(([field, label]) => <td key={field} className="p-2">{cell(field, index, row[field], `${row.statName} ${label}`)}</td>)}
              </tr>)}
            </tbody>
          </table>
        </div>
      </>}
    </div>
  </AppShell>;
}
